import { Link, useLocation } from 'react-router-dom'
import { Gift, Home, Trophy, LogIn, ClipboardList, Menu, X, Clock, User, Settings, LogOut } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import SessionStatus from './SessionStatus'
import { useAuth } from '../contexts/AuthContext'
import { useLogout } from '../hooks/useAuth'
import { systemService } from '../services/api'

const Header = () => {
  const location = useLocation()
  const { isAuthenticated, isAdmin, sessionTimeout, user } = useAuth()
  const logoutMutation = useLogout()
  const [serverOffset, setServerOffset] = useState<number | null>(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSessionLabel, setMobileSessionLabel] = useState('')

  const handleLogout = () => {
    logoutMutation.mutate()
  }

  const navItems = useMemo(() => {
    const items = [
      { path: '/', label: 'Início', icon: Home },
      { path: '/resultados', label: 'Resultados', icon: Trophy },
    ]

    if (isAdmin) {
      items.push({ path: '/administration', label: 'Administração', icon: ClipboardList })
    }

    return items
  }, [isAdmin])

  const formatSessionTime = (minutes: number) => {
    if (minutes <= 0) return '0min'
    const totalSeconds = Math.floor(minutes * 60)
    const hours = Math.floor(totalSeconds / 3600)
    const mins = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60

    if (hours > 0) {
      return `${hours}h ${mins.toString().padStart(2, '0')}m`
    }
    if (mins > 0) {
      return `${mins}m ${secs.toString().padStart(2, '0')}s`
    }
    return `${secs}s`
  }

  useEffect(() => {
    let isSubscribed = true

    const fetchServerTime = async () => {
      try {
        const data = await systemService.getServerTime()
        if (!isSubscribed) return

        const offset = data.epochMillis - Date.now()
        setServerOffset(offset)
        setCurrentTime(new Date(Date.now() + offset))
      } catch (error: any) {
        // Não logar erros de rede/CORS (são esperados em alguns casos)
        const isNetworkError = error?.code === 'ERR_NETWORK' || 
                              error?.message?.includes('CORS') ||
                              error?.message?.includes('Network Error')
        
        if (!isNetworkError) {
          console.warn('Erro ao obter hora do servidor (usando hora local):', error)
        }
        
        if (!isSubscribed) return
        // Usar hora local se não conseguir obter do servidor
        setServerOffset(null)
      }
    }

    // Só tentar buscar hora do servidor se estiver autenticado
    if (isAuthenticated) {
      fetchServerTime()
      const refreshInterval = setInterval(fetchServerTime, 60 * 1000)

      return () => {
        isSubscribed = false
        clearInterval(refreshInterval)
      }
    } else {
      // Se não estiver autenticado, usar hora local
      setServerOffset(null)
    }
  }, [isAuthenticated])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(() => {
        const baseTime = serverOffset !== null ? Date.now() + serverOffset : Date.now()
        return new Date(baseTime)
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [serverOffset])

  useEffect(() => {
    const updateLabel = () => {
      const remaining = sessionTimeout.getTimeRemaining()
      setMobileSessionLabel(formatSessionTime(remaining))
    }

    updateLabel()
    const interval = setInterval(updateLabel, 1000)

    return () => clearInterval(interval)
  }, [sessionTimeout])

  const formattedDateTime = useMemo(() => {
    return `${currentTime.toLocaleDateString('pt-BR')} ${currentTime.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })}`
  }, [currentTime])

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile */}
        <div className="flex items-center justify-between md:hidden py-3">
          <div className="flex items-center space-x-3">
            <button
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
              onClick={() => setMobileMenuOpen(prev => !prev)}
              aria-label="Abrir menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <Link to="/" className="flex items-center space-x-2">
              <Gift className="h-8 w-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">FENAE Sorteios</span>
            </Link>
          </div>
          {isAuthenticated ? (
            <span className="inline-flex flex-col items-start text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded-md">
              <span className="inline-flex items-center space-x-1">
                <Clock className="h-3 w-3" />
                <span>Sessão: {mobileSessionLabel}</span>
              </span>
              <span className="text-[11px] text-blue-700/80">{formattedDateTime}</span>
            </span>
          ) : (
            <span className="text-xs text-gray-500">{formattedDateTime}</span>
          )}
        </div>

        {/* Desktop */}
        <div className="hidden md:flex items-center justify-between h-16">
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center space-x-2">
              <Gift className="h-8 w-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">FENAE Sorteios</span>
            </Link>

            <nav className="flex space-x-8">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    location.pathname === path
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </Link>
              ))}
            </nav>

          </div>

          {isAuthenticated ? (
            <div className="flex items-center space-x-4">
              <div className="text-sm text-right">
                <p className="font-medium text-gray-900">{user?.nome}</p>
                <p className="text-xs text-gray-500">
                  {user?.role === 'ADMIN' ? 'Administrador' : 'Usuário'}
                </p>
              </div>
              <div className="flex flex-col items-center">
                <SessionStatus 
                  timeRemaining={sessionTimeout.getTimeRemaining()}
                  onExtendSession={sessionTimeout.resetTimeout}
                />
                <span className="text-xs text-gray-500 mt-1">{formattedDateTime}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to="/profile"
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Perfil"
                >
                  <User className="h-5 w-5" />
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Painel Administrativo"
                  >
                    <Settings className="h-5 w-5" />
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  disabled={logoutMutation.isLoading}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                >
                  <LogOut className="h-4 w-4" />
                  <span>{logoutMutation.isLoading ? 'Saindo...' : 'Sair'}</span>
                </button>
              </div>
            </div>
          ) : (
            <Link
              to="/login"
              className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
            >
              <LogIn className="h-4 w-4" />
              <span>Entrar</span>
            </Link>
          )}
        </div>
      </div>
      
      {/* Navegação mobile */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 space-y-2">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === path
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-3 text-gray-600">
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex flex-col items-center justify-center space-y-1 px-3 py-2 rounded-md text-xs font-medium hover:text-primary-600 hover:bg-primary-50 transition-colors"
                  >
                    <User className="h-5 w-5" />
                    <span>Perfil</span>
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex flex-col items-center justify-center space-y-1 px-3 py-2 rounded-md text-xs font-medium hover:text-primary-600 hover:bg-primary-50 transition-colors"
                    >
                      <Settings className="h-5 w-5" />
                      <span>Painel</span>
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false)
                      handleLogout()
                    }}
                    disabled={logoutMutation.isLoading}
                    className="flex flex-col items-center justify-center space-y-1 px-3 py-2 rounded-md text-xs font-medium hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>{logoutMutation.isLoading ? 'Saindo' : 'Sair'}</span>
                  </button>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <LogIn className="h-4 w-4" />
                <span>Entrar</span>
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  )
}

export default Header
